import { DateType } from "@date-io/type";
import { TextField, TextFieldProps } from "@mui/material";
import { DateTimePicker, DateTimePickerProps } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import _ from "lodash";
import { useEffect } from "react";
import { Control, Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

type FormDateTimePickerProps<IFormValues extends FieldValues = FieldValues> = {
    name: Path<IFormValues>,
    control?: Control<IFormValues, any>,
    shouldUnregister?: boolean,
    label?: string | JSX.Element,
    textFieldProps?: Omit<TextFieldProps, 'name' | 'value' | 'error' | 'FormHelperTextProps'>
} & Omit<DateTimePickerProps<string, DateType>, 'renderInput' | 'value' | 'ref' | 'label' | 'onChange'> & {
    onChange?: DateTimePickerProps<string, DateType>['onChange']
}

const FormDateTimePicker = <IFormValues extends FieldValues = FieldValues>({
    name,
    control,
    shouldUnregister,
    label,
    textFieldProps,
    ...props
}: FormDateTimePickerProps<IFormValues>) => {

    const { t } = useTranslation('booking');

    const formContext = useFormContext<IFormValues>();

    useEffect(() => {
        return () => {
            const innerControl = control ?? formContext.control;
            const currentValue: Dayjs | null = _.get(innerControl._formValues, name);
            if (currentValue && !currentValue.isValid()) {
                _.set(innerControl._formValues, name, null);
            }
        }
    }, []);

    return (
        <Controller
            name={name}
            control={control ?? formContext.control}
            shouldUnregister={shouldUnregister}
            render={({ field, fieldState, formState }) =>
                <DateTimePicker
                    {...props}
                    ref={field.ref}
                    value={field.value}
                    onChange={(value) => {
                        field.onChange(value);
                        props.onChange?.(value);
                    }}
                    label={label}
                    inputFormat={props.inputFormat ?? 'L HH:mm'}
                    ampm={props.ampm ?? false}
                    disabled={props.disabled ?? formState.isSubmitting}
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            {...textFieldProps}
                            onBlur={field.onBlur}
                            error={Boolean(fieldState.error)}
                            FormHelperTextProps={{
                                component: "div"
                            } as any}
                            helperText={fieldState.error?.message ? t(fieldState.error.message) : textFieldProps?.helperText}
                            fullWidth
                        />
                    }
                />
            }
        />
    )
};

export default FormDateTimePicker;